import { Schema, SchemaGetter } from "effect";

/** Objects and callable objects, without inspecting their properties. */
export const ReferenceValue = /* @__PURE__ */ (() => Schema.declare((value): value is object =>
  value !== null && (typeof value === "object" || typeof value === "function")
))();

/** Inspect property metadata without reading caller-owned property values. */
export const OwnPropertyDescriptors = /* @__PURE__ */ (() => ReferenceValue.pipe(Schema.decodeTo(
  Schema.Record(Schema.Union([Schema.String, Schema.Symbol]), Schema.Unknown),
  {
    decode: SchemaGetter.transform(value => Object.getOwnPropertyDescriptors(value)),
    encode: SchemaGetter.forbidden(() => "Property inspection does not reconstruct caller-owned objects.")
  }
)))();

export const OwnPropertyDescriptor = /* @__PURE__ */ (() => Schema.Tuple([
  ReferenceValue, Schema.Union([Schema.String, Schema.Symbol])
]).pipe(Schema.decodeTo(Schema.UndefinedOr(Schema.ObjectKeyword), {
  decode: SchemaGetter.transform(([value, key]) => Object.getOwnPropertyDescriptor(value, key)),
  encode: SchemaGetter.forbidden(() => "Property inspection does not reconstruct caller-owned objects.")
})))();

export const ObjectPrototype = /* @__PURE__ */ (() => ReferenceValue.pipe(Schema.decodeTo(Schema.Union([ReferenceValue, Schema.Null]), {
  decode: SchemaGetter.transform(value => Object.getPrototypeOf(value)),
  encode: SchemaGetter.forbidden(() => "Prototype inspection does not reconstruct caller-owned objects.")
})))();

/** The runtime kind is metadata, including the historical `object` kind of null. */
export const RuntimeValueKind = /* @__PURE__ */ (() => Schema.Unknown.pipe(Schema.decodeTo(Schema.Literals([
  "undefined", "object", "boolean", "number", "bigint", "string", "symbol", "function"
]), {
  decode: SchemaGetter.transform(value => typeof value),
  encode: SchemaGetter.forbidden(() => "A runtime kind does not reconstruct its value.")
})))();

// Unknown is required: an own data property containing undefined is different
// from an accessor descriptor that has no value property at all.
export const DataPropertyDescriptor = /* @__PURE__ */ (() => Schema.Struct({ value: Schema.Unknown }))();
